import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button, Icon, Modal } from "@legal-500/legal500-design-system";
import styles from "./Modal.module.css";

type ConfirmationModalProps = {
  title: string;
  message: string;
  warningMessage: string;
  affectedItems?: Array<{
    id: string | number;
    name: string;
    code?: string;
  }>;
  /**
   * Extra content between the warning and the footer — for an action the
   * dialog wants to offer alongside confirm/cancel rather than instead of it.
   */
  children?: ReactNode;
  confirmButtonText?: string;
  confirmButtonColor?: "default" | "error";
  cancelButtonText?: string;
  size?: "small" | "medium" | "large";
  backdropClick?: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
};

export function ConfirmationModal({
  title,
  message,
  warningMessage,
  affectedItems = [],
  children,
  confirmButtonText = "Confirm",
  confirmButtonColor = "default",
  cancelButtonText = "Cancel",
  size = "small",
  backdropClick = true,
  onClose,
  onConfirm,
}: ConfirmationModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Tracks only whether `onConfirm` is still pending, which `loading` cannot:
   * `loading` deliberately stays set after a successful confirm, so the footer
   * cannot be used twice while the parent tears the dialog down.
   */
  const [inFlight, setInFlight] = useState(false);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cancelButtonRef.current?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  const handleConfirm = async () => {
    setInFlight(true);
    setLoading(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      setLoading(false);
    } finally {
      setInFlight(false);
    }
  };

  /**
   * Closing is refused while the confirmed action is still running. The footer's
   * Cancel is already disabled then, but the backdrop, Escape and the header's
   * close button all reach `onClose` directly — and dismissing does not stop the
   * action, it only unmounts the dialog that owns the result, so the outcome of
   * something the user explicitly confirmed would be reported to nobody.
   */
  const handleClose = () => {
    if (!inFlight) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={handleClose}
      title={title}
      size={size}
      blurBackground
      backdropClick={backdropClick}
    >
      <div className={styles.deleteModal}>
        <p className={styles.deleteMessage}>{message}</p>
        {warningMessage ? <p className={styles.deleteWarning}>{warningMessage}</p> : null}
        {affectedItems.length > 0 && (
          <div className={styles.deleteList}>
            <p className={styles.deleteListTitle}>The following items will be affected:</p>
            <ul className={styles.deleteListItems}>
              {affectedItems.map(item => (
                <li key={item.id}>
                  <strong>{item.name}</strong>
                  {item.code ? ` (${item.code})` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
        {children}
        {error && (
          <div className={styles.formError}>
            <Icon icon="exclamation" size="small" />
            <span>{error}</span>
          </div>
        )}
        <div className={styles.modalFooter}>
          <Button
            ref={cancelButtonRef}
            onClick={onClose}
            variant="outlined"
            color="default"
            disabled={loading}
            aria-label={cancelButtonText}
          >
            {cancelButtonText}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="default"
            className={confirmButtonColor === "error" ? styles.dangerButtonFilled : undefined}
            disabled={loading}
            aria-label={confirmButtonText}
          >
            {loading ? "Processing..." : confirmButtonText}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
